import React, { FC, lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Box } from '@mui/material';
import NotFoundPage from './pages/not-found/not-found.page';

const CHUNK_RELOAD_KEY = 'origin-verify-chunk-reload-attempted';

// eslint-disable-next-line @typescript-eslint/comma-dangle, @typescript-eslint/explicit-function-return-type
const lazyWithChunkRecovery = <T,>(factory: () => Promise<{ default: T }>) => (
	lazy(async () => {
		try {
			const module = await factory();
			sessionStorage.removeItem(CHUNK_RELOAD_KEY);

			return module;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			const isChunkLoadError = (/ChunkLoadError|Loading chunk [\d]+ failed/i).test(errorMessage);
			const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';

			if (isChunkLoadError && !hasReloaded) {
				sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
				window.location.reload();
			}

			throw error;
		}
	})
);

// eslint-disable-next-line @typescript-eslint/promise-function-async
const ValidatorPage = lazyWithChunkRecovery(() =>
	import('./pages/validator/validator.component').then(module => ({ 'default': module.ValidatorPage })));

const Root: FC = () => {
	const [t] = useTranslation();

	const routes = createBrowserRouter([
		{
			path: '/',
			element: (
				<Suspense fallback={
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
						<CircularProgress />
					</Box>
				}>
					<ValidatorPage />
				</Suspense>
			)
		},
		{ path: '*', element: <NotFoundPage /> },
	]);

	return (
		<>
			<Helmet title={t('nav.root-title') ?? ''}>
				<meta
					name="viewport"
					content="width=device-width, maximum-scale=1, minimum-scale=1, initial-scale=1, user-scalable=no"
				/>
			</Helmet>
			<RouterProvider router={routes} />
		</>
	);
};

export default Root;
